// import { isExternalNotApproved } from 'utils/checker/entity';
import Box from "@material-ui/core/Box";
import { CustomFieldShow } from "components/CustomFields/CustomFieldShow";
import { CommodityField } from "components/fields/CommodityField";
import { CoolerTypeField } from "components/fields/CoolerTypeField";
import { OrderStatusEditField } from "components/inputs/OrderStatusEditField";
import { useTerminalsContext } from "contexts/TerminalsProvider";
import React, { useMemo, useState } from "react";
import {
	Loading,
	NumberField,
	ReferenceField,
	RichTextField,
	Tab,
	TextField,
	useRecordContext,
	useTranslate,
} from "react-admin";
import { isStorageModuleEnabled } from "utils/storageModule";

import { CountryISOCodeField } from "../../../components/fields/CountryISOCodeField";
import { DateFormatField } from "../../../components/fields/DateFormatField";
import { DocumentsFieldBlock } from "../../../components/fields/DocumentsField";
import { GenerateDocumentsOutputButton } from "../../../components/fields/DocumentsField/GenerateDocumentsOutputButton";
import {
	ReferenceEditableField,
	TextEditableField,
} from "../../../components/fields/EditableField";
import { LabelSourceField } from "../../../components/fields/LabelSourceField";
import { LongTextField } from "../../../components/fields/LongTextField";
import { OrderStatusField } from "../../../components/fields/OrderStatusField";
import { PriorityField } from "../../../components/fields/PriorityField";
import { SelectEnumField } from "../../../components/fields/SelectEnumField";
import { SelectTextField } from "../../../components/fields/SelectTextField";
import TransDriverField from "../../../components/fields/TransDriverField";
import { UserFullNameReferenceField } from "../../../components/fields/UserFullNameReferenceField";
import { YesNoField } from "../../../components/fields/YesNoField";
import { FlexLayout, FlexRow } from "../../../components/FlexShowLayout";
import { FlexBlock, FlexBlockContainer, FlexBlockRow } from "../../../components/FlexShowLayout";
import {
	OrderDocumentOutputType,
	OrderStatus,
	TransporterRoles,
	UserRoleByCompanyType,
} from "../../../entities";
import { HideableOrderField } from "../../../entities/HideableOrderField";
import {
	OrderService,
	OrderServiceNames,
	perEligibleServices,
} from "../../../entities/OrderService";
import { UserRole } from "../../../entities/UserRole";
import { useTerminalFieldVisibility } from "../../../hooks/useTerminalFieldVisibility";
import ChatContainer from "../Chat";
import { ColdStorageShowSection } from "./GeneralTabSections/ColdStorageSection";
import { ETDShowSection } from "./GeneralTabSections/ETDSection";
import { HistoryField, OrderPricelistHistoryField } from "./History";
import { OrderHistoryGrid } from "./History/OrderHistoryGrid";
import { OrderPricelistHistoryGrid } from "./History/OrderPricelistHistoryGrid";
import { PricelistField } from "./Pricelist/";

const formatDriverLabel = (driverData) => {
	const driverFirstName = driverData?.["first_name"] ?? "";
	const driverLastName = driverData?.["last_name"] ?? "";

	let label = "  ";
	if (driverFirstName || driverLastName) {
		const _label = `${driverFirstName} ${driverLastName}`.trim();
		if (_label.length) {
			label = _label;
		}
	}

	return label;
};

const TransDriverFieldWrapper = (props) => {
	const [driverData, setDriverData] = useState({});

	const handleNestedResourceData = (_driverData) => {
		setDriverData(_driverData.data);
	};

	const label = useMemo(
		() => formatDriverLabel(driverData),
		[driverData?.["first_name"], driverData?.["last_name"]]
	);

	return (
		<TransDriverField
			{...props}
			refSource={`${props.prefix}_driver_id`}
			label={label}
			handleNestedResourceData={handleNestedResourceData}
		/>
	);
};

export const PlugTab = () => <div />;

export const GeneralTab = ({
	isOSFCManager,
	isCustomerAsTrans,
	isPricelistNeed = true,
	...props
}) => {
	const { isFieldHidden } = useTerminalFieldVisibility(props.record?.terminal_id);
	const { data: terminals } = useTerminalsContext();
	const usesStorageModule = isStorageModuleEnabled(terminals[props.record?.terminal_id]);

	// if (isPricelistNeed === null && props["record"] && props["permissions"]){
	// 	isPricelistNeed = props["record"]["company_id"] === props["permissions"]["company_id"]
	// }
	const translate = useTranslate();
	const isRoleForTerminalView =
		props.permissions &&
		(props.permissions.role === UserRole.admin ||
			props.permissions.role === UserRole.app_manager ||
			props.permissions.role === UserRole.osfc_manager ||
			props.permissions.role === UserRole.osfc_employee ||
			props.permissions.role === UserRole.customer_manager ||
			props.permissions.role === UserRole.customer_employee ||
			props.permissions.role === UserRole.customer_and_transporter_manager ||
			props.permissions.role === UserRole.customer_and_transporter_employee);

	const isNot3rdPartyRole =
		UserRoleByCompanyType.customer_and_transporter_roles.indexOf(props.permissions.role) === -1;

	const isPriceListField =
		!isCustomerAsTrans &&
		props?.record?.status !== OrderStatus.DRAFT &&
		TransporterRoles.indexOf(props.permissions.role) === -1;
	// const isIsExternalNotApproved = isExternalNotApproved(props?.record?.state);
	const isColdStorage = props?.record?.service === OrderService.coldStorage;
	return (
		<Tab label="General" {...props}>
			<FlexLayout>
				<FlexRow isNot3rdPartyRole={isNot3rdPartyRole}>
					{isOSFCManager ? (
						<ReferenceField source="company_id" reference="customer-companies" link={false}>
							<TextField source="full_name" />
						</ReferenceField>
					) : null}
					<TextField source="id" />
					<TextField source="reference" />
					<SelectEnumField source="service" enumObj={OrderServiceNames} translate={false} reverse />
					<PriorityField source="priority" />
					{!isFieldHidden(HideableOrderField.IS_PER) &&
						perEligibleServices.includes(props.record?.service) && <YesNoField source="is_per" />}
					<OrderStatusField
						addLabel
						label={translate("resources.orders.fields.status")}
						source="status"
						editable={isOSFCManager}
						permissions={props.permissions}
						text_field_source="short_name"
					/>
					{isRoleForTerminalView ? (
						<ReferenceField source="terminal_id" reference="terminal" link={false}>
							<TextField label="Terminal" source="name" />
						</ReferenceField>
					) : null}
				</FlexRow>

				<FlexBlockContainer>
					<FlexBlock title={translate("resources.orders.block-label.eta")} variant="left">
						<FlexBlockRow>
							<DateFormatField source="eta_date" />
							<TextField source="eta_time" />
							<TextField source="eta_slot_time" />
						</FlexBlockRow>
						<FlexBlockRow>
							{!isFieldHidden(HideableOrderField.PLACE_FROM) && (
								<LongTextField source="place_from" />
							)}
							{!isFieldHidden(HideableOrderField.PLACE_FROM_COUNTRY_CODE) && (
								<CountryISOCodeField source="place_from_country_code" />
							)}
							<TextEditableField editable={isOSFCManager} source="port_in" />
							{!isFieldHidden("eta_external_terminal_id") && (
								<ReferenceField
									source="eta_external_terminal_id"
									reference="external-terminal-links"
									link={false}
								>
									<TextField source="external_name" />
								</ReferenceField>
							)}
						</FlexBlockRow>
						<FlexBlockRow>
							{!isFieldHidden(HideableOrderField.ETA_TRUCK) && (
								<SelectTextField source="eta_truck" refSource="eta_truck_id" reference="trucks" />
							)}
							{!isFieldHidden(HideableOrderField.ETA_DRIVER) &&
								(isNot3rdPartyRole ? (
									<SelectTextField
										source="eta_driver"
										refSource="eta_driver_id"
										reference="drivers"
										formatLabel={formatDriverLabel}
									/>
								) : (
									<TransDriverFieldWrapper source="eta_driver" prefix="eta" addLabel />
								))}
							{!isFieldHidden(HideableOrderField.ETA_DRIVER_PHONE) && (
								<TextField source="eta_driver_phone" />
							)}
						</FlexBlockRow>
						<FlexBlockRow justify="flex-start">
							{isNot3rdPartyRole && !isFieldHidden(HideableOrderField.ETA_SHOW_DOC_FOR_TRANS) ? (
								<YesNoField
									source="eta_show_doc_for_trans"
									label="resources.orders.fields.eta_show_doc"
								/>
							) : null}
							{isNot3rdPartyRole ? (
								<YesNoField
									source="is_eta_3rd_party_reload"
									label="resources.orders.fields.is_eta_3rd_party_reload"
								/>
							) : null}
							{!isFieldHidden(HideableOrderField.INCOMING_TRIP_NUMBER) && (
								<TextField source="incoming_trip_number" />
							)}
							{!isFieldHidden(HideableOrderField.UNLOADING_SEQUENCE) && (
								<NumberField source="unloading_sequence" />
							)}
						</FlexBlockRow>
						<FlexBlockRow>
							{!isFieldHidden(HideableOrderField.COMMODITY_ID) && (
								<CommodityField
									addLabel
									label={translate("resources.customer-companies.fields.commodity_id")}
									source="commodity_id"
								/>
							)}
							{!isFieldHidden(HideableOrderField.COOLER_TYPE_ID) && <CoolerTypeField />}
							{!isFieldHidden(HideableOrderField.PALLETS) && <TextField source="pallets" />}
							{!isFieldHidden(HideableOrderField.BOXES) && <TextField source="boxes" />}
							{!isFieldHidden(HideableOrderField.KILOS) && <TextField source="kilos" />}
						</FlexBlockRow>
					</FlexBlock>
					{/* --------------------- ETD/Cold storage section --------------------- */}
					<FlexBlock
						title={
							isColdStorage
								? translate("resources.orders.block-label.cold_storage")
								: translate("resources.orders.block-label.etd")
						}
						variant="right"
						bgColor={isColdStorage ? "rgba(0, 119, 255, 0.1)" : null}
						borderColor={isColdStorage ? "rgba(0, 119, 255, 0.5)" : null}
					>
						{!isColdStorage
							? ETDShowSection({
									isOSFCManager,
									isNot3rdPartyRole,
									formatDriverLabel,
									TransDriverFieldWrapper,
									isFieldHidden,
									usesStorageModule,
									...props,
							  })
							: ColdStorageShowSection({ isNot3rdPartyRole, ...props })}
					</FlexBlock>
					{/* --------------------- End of ETD/Cold storage section --------------------- */}
				</FlexBlockContainer>

				{/* <FlexRow isNot3rdPartyRole={isNot3rdPartyRole}>
					{!isFieldHidden(HideableOrderField.SPLIT_LOAD) && <YesNoField source="split_load" />}
					{!isFieldHidden("loading_sequence") && <NumberField source="loading_sequence" />}
					{!isFieldHidden("outgoing_trip_number") && <NumberField source="outgoing_trip_number" />}
				</FlexRow> */}

				<FlexRow isNot3rdPartyRole={isNot3rdPartyRole}>
					<RichTextField source="notes" flex />
					<RichTextField source="internal_notes" styleWarning flex />
				</FlexRow>

				{props?.record?.custom_fields && props?.record?.custom_fields.length !== 0 && (
					<FlexRow isNot3rdPartyRole={isNot3rdPartyRole} wrapText={true}>
						{props.record.custom_fields.map((field) => (
							<CustomFieldShow key={field.field_id || field.id} field={field} />
						))}
					</FlexRow>
				)}
				{isPriceListField && (
					<FlexRow isNot3rdPartyRole={isNot3rdPartyRole}>
						<PricelistField
							label="resources.pricelist-articles.name"
							reference="order-pricelist"
							target="id"
							permissions={props.permissions}
						/>
					</FlexRow>
				)}
			</FlexLayout>
		</Tab>
	);
};

export const OrderHistoryTab = (props) => {
	return (
		<Tab label="Order History" {...props}>
			<UserFullNameReferenceField label="resources.entity-editlog.created_by" />
			<HistoryField
				label="resources.order-history.name"
				reference="order-history"
				target="original_id"
			>
				<OrderHistoryGrid />
			</HistoryField>
		</Tab>
	);
};

export const OrderPrcelistHistoryTab = (props) => {
	return (
		<Tab label="Order Pricelist History" {...props}>
			<UserFullNameReferenceField label="resources.entity-editlog.created_by" />
			<HistoryField
				label="resources.order-pricelist-history.name"
				reference="order-pricelist"
				target="id"
				filter={{ include_permanently_deleted: true }}
			>
				<OrderPricelistHistoryGrid
					rowClick="expand"
					expand={<OrderPricelistHistoryField />}
					isRowExpandable={(record) => record.has_changes}
				/>
			</HistoryField>
		</Tab>
	);
};

export const DocumentsTab = ({ isOSFCManager, isCustomerAsTrans, ...props }) => {
	// const isIsExternalNotApproved = isExternalNotApproved(props?.record?.state);

	return (
		<Tab label="Documents" {...props}>
			<FlexLayout>
				<FlexRow>
					{isOSFCManager ? (
						<ReferenceField source="company_id" reference="customer-companies" link={false}>
							<TextField source="full_name" />
						</ReferenceField>
					) : null}
					<TextField source="id" />
					<TextField source="reference" />
					<PriorityField source="priority" />
					{/* <ReferenceEditableField editable={isOSFCManager && !isIsExternalNotApproved} source="status" reference="order-statuses" */}
					<ReferenceEditableField
						editable={isOSFCManager}
						source="status"
						reference="order-statuses"
						editField={(rec) => <OrderStatusEditField data={rec} permissions={props.permissions} />}
					>
						<TextField source="short_name" />
					</ReferenceEditableField>
					<OrderStatusField
						source="status"
						// editable={isOSFCManager && !isIsExternalNotApproved}
						editable={isOSFCManager}
						permissions={props.permissions}
						text_field_source="short_name"
					/>
				</FlexRow>
			</FlexLayout>
			<DocumentsFieldBlock
				variant="elevation"
				isCustomerAsTrans={isCustomerAsTrans}
				isOutputDocuments={false}
				showDownloadAll={true}
			/>
		</Tab>
	);
};

export const unloadingDisable = (record) =>
	// !(record.service === OrderService.intoPlukkStorage && record.eta_date && record.eta_time);
	!record.eta_date;
export const loadingDisable = (record) =>
	// !(record.service === OrderService.intoPlukkStorage && record.etd_date && record.etd_time);
	!record.etd_date;

export const OutDocumentsTab = ({
	isOSFCManager,
	isCustomerAsTrans,
	onOutDocWarnings,
	...props
}) => {
	const translate = useTranslate();
	const record = useRecordContext();
	const [isLoading, setIsLoading] = useState(false);

	const [isLoadingDoc, setIsLoadingDoc] = React.useState(false);
	const [isUnloadingDoc, setIsUnloadingDoc] = React.useState(false);

	const changeIsLoading = (isLoadingDocFlag) => {
		setIsLoadingDoc(isLoadingDocFlag);
	};
	const changeIsUnloading = (isLoadingDocFlag) => {
		setIsUnloadingDoc(isLoadingDocFlag);
	};

	const unloading_disable = unloadingDisable(record);
	const loading_disable = loadingDisable(record);

	if (isLoading) {
		return (
			<Loading
				loadingPrimary={translate("osfc.circilar-progress.out_doc.generated")}
				loadingSecondary=""
			/>
		);
	}

	return (
		<Tab label="tab.out-documents.name" {...props}>
			<Box display="flex" flex="1" justifyContent="center" alignItems="center">
				<GenerateDocumentsOutputButton
					type={OrderDocumentOutputType.unloading}
					isLoading={isLoading}
					setIsLoading={setIsLoading}
					disabled={unloading_disable}
					order={record}
					buttonText="resources.output-document.button.unloading.title"
					margin={3}
					isOutputDoc={isUnloadingDoc}
					onOutDocWarnings={onOutDocWarnings}
				/>
				<GenerateDocumentsOutputButton
					type={OrderDocumentOutputType.loading}
					isLoading={isLoading}
					setIsLoading={setIsLoading}
					disabled={loading_disable}
					order={record}
					buttonText="resources.output-document.button.loading.title"
					isOutputDoc={isLoadingDoc}
					onOutDocWarnings={onOutDocWarnings}
				/>
			</Box>
			<DocumentsFieldBlock
				variant="elevation"
				isCustomerAsTrans={isCustomerAsTrans}
				isOutputDocuments={true}
				changeIsLoading={changeIsLoading}
				changeIsUnloading={changeIsUnloading}
			/>
		</Tab>
	);
};

export const ChatTab = ({ isOSFCManager, ...props }) => {
	return (
		<Tab label="Chat" {...props}>
			<ChatContainer />
		</Tab>
	);
};
